import { scaleOrdinal } from 'd3-scale';
import { useMemo, useRef } from 'react';
import type {
  ChoicesData,
  ChoicesStaticOptions,
  ChoicesVisualization,
} from '../types';
import { getLongestLabel } from '../utils/get-longest-label';
import type { MultipleChoiceDotsRenderChoiceVisualization } from '../utils/types/render-options-by-slide-type/multiple-choice';
import type { RenderChoiceLabel } from '../utils/types/render-options-by-slide-type/types';
import { MAX_NUMBER_OF_DOTS_WITHOUT_LAG } from './constants';
import { DotsGrid } from './dots-grid';
import { calculateDotsGridDimensions } from './dots-grid/calculateDotsGridDimensions';
import { Option } from './option';
import { getScoreDisplayText } from './score/getScoreDisplayText';

interface DotsProps extends ChoicesVisualization {
  renderChoiceVisualization?:
    | MultipleChoiceDotsRenderChoiceVisualization
    | undefined;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
  isScoreHidden: boolean;
}

export const Dots = ({
  width,
  height,
  colors,
  data,
  staticOptions,
  disableAnimations,
  keyAccessor = (d: ChoicesData | ChoicesStaticOptions) => d.id.toString(),
  labelAccessor = (d: ChoicesStaticOptions) => d.label,
  valueAccessor = (d: ChoicesData) => d.score,
  isScoreHidden,
  fontFamily,
  textColor,
  shouldShowCorrect,
  shouldShowScorePercentage,
  renderChoiceVisualization,
  renderChoiceLabel,
}: DotsProps) => {
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);
  const rootHeight = Math.max(height, 0);
  const rootWidth = Math.max(width, 0);

  const discreteValuesBasedOnId = staticOptions.map(keyAccessor);
  const fillColors = scaleOrdinal(colors).domain(discreteValuesBasedOnId);

  const longestLabelId = getLongestLabel(
    staticOptions,
    labelAccessor,
    keyAccessor,
  );

  const largestOptionScore = Math.max(...data.map(valueAccessor), 0);
  const count = staticOptions.length;

  const dimensions = useMemo(() => {
    return calculateDotsGridDimensions({
      width: rootWidth,
      height: rootHeight,
      count,
      largestNumberOfDots: Math.min(
        largestOptionScore,
        MAX_NUMBER_OF_DOTS_WITHOUT_LAG,
      ),
    });
  }, [count, largestOptionScore, rootHeight, rootWidth]);

  if (rootWidth <= 0 || rootHeight <= 0) {
    return null;
  }

  return (
    <DotsGrid
      width={rootWidth}
      height={rootHeight}
      ref={labelsContainerRef}
      style={{ opacity: 0 }}
      largestNumberOfDots={Math.min(
        largestOptionScore,
        MAX_NUMBER_OF_DOTS_WITHOUT_LAG,
      )}
      data-testid="dots-visualization"
    >
      {staticOptions.map((option) => {
        const dataValue = data.find((d) => d.id === option.id);
        const choiceId = keyAccessor(option);
        const isLongestLabel = longestLabelId === choiceId;
        const score = dataValue ? valueAccessor(dataValue) : 0;
        const cappedScore = Math.min(score, MAX_NUMBER_OF_DOTS_WITHOUT_LAG);

        const visualization = (
          <Option
            choiceId={choiceId}
            key={choiceId}
            width={0}
            height={0}
            label={labelAccessor(option)}
            color={fillColors(choiceId)}
            scores={cappedScore}
            scoreDisplayText={getScoreDisplayText(
              score,
              shouldShowScorePercentage,
            )}
            shouldRenderTextResizer={
              isLongestLabel ? { labelsContainerRef } : false
            }
            isCorrect={option.isMarkedCorrect}
            fontFamily={fontFamily}
            textColor={textColor}
            shouldShowCorrect={shouldShowCorrect}
            disableAnimations={disableAnimations}
            shouldHideScores={isScoreHidden}
            renderChoiceLabel={renderChoiceLabel}
          />
        );

        const visualizationOverride = renderChoiceVisualization?.({
          node: visualization,
          id: choiceId,
          ...(dimensions &&
            (({ childWidth, childHeight, ...rest }) => ({
              ...rest,
              width: childWidth,
              height: childHeight,
            }))(dimensions)),
        });

        return visualizationOverride ?? visualization;
      })}
    </DotsGrid>
  );
};
